import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { clearSessionKeys } from "../../helpers/sessionStorageKeys";

// Flag lida (e apagada) pela tela de login para mostrar "conta excluída".
// Não está em SESSION_KEYS de propósito: precisa sobreviver ao handleLogout.
export const ACCOUNT_DELETED_KEY = "accountDeleted";

const useStyles = makeStyles((theme) => ({
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  warning: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  list: {
    margin: theme.spacing(1, 0, 2),
    paddingLeft: theme.spacing(2.5),
    "& li": {
      marginBottom: theme.spacing(0.5),
    },
  },
  confirmButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

/**
 * "Excluir minha conta" (exigência Apple 5.1.1(v)).
 * Busca a prévia no backend para listar exatamente o que vai acontecer — só o
 * usuário, ou a empresa inteira quando ele é o último administrador.
 */
const DeleteAccountModal = ({ open, onClose }) => {
  const classes = useStyles();

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setPreview(null);
    setPassword("");
    setShowPassword(false);
    setAcknowledged(false);
    setSubmitting(false);
    setLoadingPreview(true);

    api
      .get("/auth/delete-account/preview")
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        toastError(err);
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
    // `onClose` fica fora das deps de propósito: o layout passa uma arrow nova a
    // cada render e a prévia seria refeita em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!preview || !password || !acknowledged || submitting) return;
    setSubmitting(true);
    try {
      // Setado ANTES da requisição: o backend emite force-logout via socket e o
      // hook de auth pode redirecionar para /login antes da resposta HTTP chegar.
      localStorage.setItem(ACCOUNT_DELETED_KEY, "1");
      await api.post("/auth/delete-account", { password });
      toast.success(i18n.t("deleteAccountModal.toasts.success"));
      // Logout local imediato e determinístico: a conta já não existe, então
      // não dependemos do socket nem do DELETE /auth/logout (que falharia com
      // 401 e atrasaria o redirect). O pequeno atraso é só para o toast de
      // confirmação ser visto antes de voltar para o login.
      setTimeout(() => {
        clearSessionKeys();
        window.location.href = "/login";
      }, 800);
    } catch (err) {
      localStorage.removeItem(ACCOUNT_DELETED_KEY);
      toastError(err);
      setSubmitting(false);
    }
  };

  const isCompanyScope = preview?.scope === "company";
  const canConfirm = !!preview && !!password && acknowledged && !submitting;

  const renderConsequences = () => {
    if (isCompanyScope) {
      return (
        <ul className={classes.list}>
          <li>
            <Typography variant="body2">
              {i18n.t("deleteAccountModal.companyScope.company", {
                companyName: preview.companyName || "",
                interpolation: { escapeValue: false },
              })}
            </Typography>
          </li>
          {preview.otherActiveUsers > 0 && (
            <li>
              <Typography variant="body2">
                {i18n.t("deleteAccountModal.companyScope.users", {
                  total: preview.otherActiveUsers,
                })}
              </Typography>
            </li>
          )}
          {preview.whatsappConnections > 0 && (
            <li>
              <Typography variant="body2">
                {i18n.t("deleteAccountModal.companyScope.connections", {
                  total: preview.whatsappConnections,
                })}
              </Typography>
            </li>
          )}
          {preview.hasActiveSubscription && (
            <li>
              <Typography variant="body2">
                {i18n.t("deleteAccountModal.companyScope.subscription")}
              </Typography>
            </li>
          )}
          <li>
            <Typography variant="body2">
              {i18n.t("deleteAccountModal.companyScope.history")}
            </Typography>
          </li>
        </ul>
      );
    }

    return (
      <ul className={classes.list}>
        <li>
          <Typography variant="body2">
            {i18n.t("deleteAccountModal.userScope.access")}
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            {i18n.t("deleteAccountModal.userScope.tickets")}
          </Typography>
        </li>
      </ul>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-account-dialog"
    >
      <DialogTitle id="delete-account-dialog">
        {i18n.t("deleteAccountModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        {loadingPreview || !preview ? (
          <div className={classes.loading}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              {i18n.t("deleteAccountModal.intro")}
            </Typography>
            {isCompanyScope && (
              <Typography className={classes.warning} color="error">
                {i18n.t("deleteAccountModal.companyScope.intro")}
              </Typography>
            )}
            {renderConsequences()}
            <TextField
              label={i18n.t("deleteAccountModal.passwordLabel")}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              margin="dense"
              fullWidth
              autoComplete="current-password"
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Mostrar/ocultar senha"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  color="primary"
                  disabled={submitting}
                />
              }
              label={i18n.t("deleteAccountModal.acknowledge")}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="default"
          variant="outlined"
          disabled={submitting}
        >
          {i18n.t("deleteAccountModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          className={classes.confirmButton}
          disabled={!canConfirm}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            i18n.t("deleteAccountModal.buttons.confirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountModal;
